import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, FormInput, FormActions, FormSelectList } from "../Controls/Form";
import UserData from "../Controls/UserData";
import EmploymentSelectionListItem from "../ListItems/EmploymentSelectionListItem";
import Help from "../Controls/Help";
import CustomButton from "../Controls/CustomButton";
import { toast } from "react-toastify";
import API from "@src/API";
import Helpers from "../Controls/helpers";
import { refreshConnections, useConnectionsQuery } from "../../query/useConnections";
import { useEmploymentQuery } from "../../query/useEmployment";

function emptyConnection() {
  return {
    ID: 0,
    FirstName: "",
    LastName: "",
    Phone: "",
    Email: "",
    Employments: [],
    Status: "",
  };
}

/**
 * Connection form body. Prefer ConnectionEditModal for UI entry points.
 */
export default function ConnectionEdit({ connectionId = 0, onClose }) {
  const id = Number.parseInt(connectionId, 10) || 0;
  const queryClient = useQueryClient();
  const { data: connectionsData } = useConnectionsQuery();
  const { data: employmentData } = useEmploymentQuery();
  const employmentHistory = employmentData?.employmentHistory || [];
  const employers = employmentData?.employers || [];
  const [connection, updateConnection] = useState(() => emptyConnection());
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("details");
  const [subjectSkills, setSubjectSkills] = useState([]);
  const [endorseSkillId, setEndorseSkillId] = useState("");
  const [endorseLevel, setEndorseLevel] = useState("3");
  const [endorseBusy, setEndorseBusy] = useState(false);

  useEffect(() => {
    if (!id) {
      updateConnection(emptyConnection());
      return;
    }
    const found = (connectionsData?.connections || []).find((i) => Number(i.ID ?? i.id) === id);
    if (!found) return;
    updateConnection({
      ...found,
      Employments: found.Employments || found.employments || [],
    });
  }, [id, connectionsData]);

  const otherPersonId = Number(connection.PersonID ?? connection.personID) || 0;
  const coworkerVerified = !!(connection.CoworkerVerified ?? connection.coworkerVerified);
  const endorsementsGiven = connection.EndorsementsGiven || connection.endorsementsGiven || [];
  const endorsementsReceived =
    connection.EndorsementsReceived || connection.endorsementsReceived || [];

  useEffect(() => {
    if (!coworkerVerified || !otherPersonId || tab !== "skills") return;
    let cancelled = false;
    (async () => {
      const resp = await API.getAsync(
        `/SkillEndorsement/SubjectSkills?subjectPersonId=${otherPersonId}`
      );
      if (cancelled) return;
      if (resp?.ok && Array.isArray(resp.jsonData)) {
        setSubjectSkills(resp.jsonData);
      } else {
        setSubjectSkills([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [coworkerVerified, otherPersonId, tab]);

  function close() {
    onClose?.();
  }

  async function afterMutation() {
    await refreshConnections(queryClient);
  }

  async function onDoneAsync() {
    if (busy) return;
    setBusy(true);
    try {
      const ret = await UserData.UpdateAsync("connections", connection, true);
      if (ret?.ok || ret?.result === "Success") {
        toast.success("Connection saved");
        await afterMutation();
        close();
      } else {
        toast.error(ret?.message || "Could not save connection");
      }
    } catch (err) {
      toast.error("Error: " + err);
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    const connIdLocal = connection.ID || connection.id;
    if (!connIdLocal) {
      close();
      return;
    }
    const resp = await UserData.DeleteAsync("connections", connection);
    if (resp?.result) {
      toast.success("Connection deleted");
      await afterMutation();
      close();
    } else {
      toast.error(resp?.error || "Delete failed");
    }
  }

  const isEmploymentSelected = (empId) => {
    const id = Number(empId);
    return (connection.Employments || []).some((x) => Number(x) === id);
  };

  const status = connection?.Status ?? connection?.status;
  const statusLabel = Helpers.connectionStatusLabel(status);
  const connId = connection?.ID ?? connection?.id;

  const accept = async () => {
    const resp = await API.postAsync(`/Connection/${connId}/Accept`, {});
    if (resp && resp.ok) {
      toast.success("Connection accepted");
      updateConnection({ ...connection, Status: "Verified", status: "Verified" });
      await afterMutation();
    } else {
      toast.error(resp?.jsonData?.message || "Accept failed");
    }
  };

  const decline = async () => {
    const resp = await API.postAsync(`/Connection/${connId}/Decline`, {});
    if (resp && resp.ok) {
      toast.success("Connection declined");
      await afterMutation();
      close();
    } else {
      toast.error(resp?.jsonData?.message || "Decline failed");
    }
  };

  const onToggleAllEmployments = () => {
    const history = employmentHistory;
    const selected = connection.Employments || [];
    const allSelected =
      history.length > 0 &&
      history.every((emp) => selected.some((x) => Number(x) === Number(emp.ID)));
    updateConnection({
      ...connection,
      Employments: allSelected ? [] : history.map((emp) => emp.ID),
    });
  };

  const onClickEmployment = (empID) => {
    const id = Number(empID);
    updateConnection((prevConnection) => {
      const currentEmployments = prevConnection.Employments || [];
      const isSelected = currentEmployments.some((x) => Number(x) === id);
      const updatedEmployments = isSelected
        ? currentEmployments.filter((x) => Number(x) !== id)
        : [...currentEmployments, id];
      return { ...prevConnection, Employments: updatedEmployments };
    });
  };

  const levelNames = { 1: "Beginner", 2: "Junior", 3: "Intermediate", 4: "Senior", 5: "Guru" };

  async function saveEndorsement() {
    const skillId = Number(endorseSkillId);
    const skillLevel = Number(endorseLevel);
    if (!otherPersonId || !(skillId > 0) || !(skillLevel >= 1 && skillLevel <= 5)) {
      toast.error("Pick a skill and level");
      return;
    }
    setEndorseBusy(true);
    try {
      const resp = await API.postAsync("/SkillEndorsement", {
        subjectPersonId: otherPersonId,
        skillId,
        skillLevel,
      });
      if (resp?.ok) {
        toast.success("Endorsement saved");
        await afterMutation();
      } else {
        toast.error(resp?.jsonData?.message || resp?.message || "Endorsement failed");
      }
    } finally {
      setEndorseBusy(false);
    }
  }

  if (id > 0 && !(connection.ID || connection.id)) {
    return (
      <div className="cm-modal-embedded">
        <div className="cm-modal-body-pad">
          <p className="fieldHint">Connection not found.</p>
          <CustomButton type="button" variant="contained" color="primary" onClick={close}>
            Close
          </CustomButton>
        </div>
      </div>
    );
  }

  const isPotential = Helpers.isConnectionPotential(status);
  const history = employmentHistory;
  const sharedCount = (connection.Employments || []).length;

  return (
    <div className="cm-modal-embedded">
      <Form className="cm-modal-form" object={connection} onUpdate={updateConnection}>
        <div className="cm-modal-tabs" role="tablist" aria-label="Connection sections">
          <button
            type="button"
            role="tab"
            id="connection-tab-details"
            aria-selected={tab === "details"}
            aria-controls="connection-panel-details"
            className={tab === "details" ? "is-active" : undefined}
            data-testid="connection-tab-details"
            onClick={() => setTab("details")}
          >
            Details
          </button>
          <button
            type="button"
            role="tab"
            id="connection-tab-workspaces"
            aria-selected={tab === "workspaces"}
            aria-controls="connection-panel-workspaces"
            className={tab === "workspaces" ? "is-active" : undefined}
            data-testid="connection-tab-workspaces"
            onClick={() => setTab("workspaces")}
          >
            Shared Workspaces{sharedCount > 0 ? ` (${sharedCount})` : ""}
          </button>
          {coworkerVerified ? (
            <button
              type="button"
              role="tab"
              id="connection-tab-skills"
              aria-selected={tab === "skills"}
              aria-controls="connection-panel-skills"
              className={tab === "skills" ? "is-active" : undefined}
              data-testid="connection-tab-skills"
              onClick={() => setTab("skills")}
            >
              Skill endorsements
            </button>
          ) : null}
        </div>

        <div className="cm-modal-tab-panels">
          <div
            id="connection-panel-details"
            role="tabpanel"
            aria-labelledby="connection-tab-details"
            aria-hidden={tab !== "details"}
            className={`cm-modal-tab-panel${tab === "details" ? " is-active" : ""}`}
            {...(tab !== "details" ? { inert: "" } : {})}
          >
            <Help id="2141" />
            {statusLabel ? (
              <p className="fieldHint" data-testid="connection-status">
                Status: {statusLabel}
              </p>
            ) : null}
            {isPotential ? (
              <div className="field form-group connection-accept-actions">
                <CustomButton type="button" variant="contained" color="primary" onClick={accept}>
                  Accept
                </CustomButton>
                <CustomButton type="button" variant="contained" color="error" onClick={decline}>
                  Decline
                </CustomButton>
              </div>
            ) : null}
            <FormInput label="First Name" field="FirstName" required />
            <FormInput label="Last Name" field="LastName" required />
            <FormInput type="email" label="Email" field="Email" required />
            <FormInput label="Phone Number" field="Phone" />
          </div>

          <div
            id="connection-panel-workspaces"
            role="tabpanel"
            aria-labelledby="connection-tab-workspaces"
            aria-hidden={tab !== "workspaces"}
            className={`cm-modal-tab-panel${tab === "workspaces" ? " is-active" : ""}`}
            {...(tab !== "workspaces" ? { inert: "" } : {})}
          >
            <p className="fieldHint">
              Select the workplaces you and this person had or have in common.
            </p>
            <FormSelectList
              field="Employments"
              label={
                sharedCount + " of " + history.length + " selected"
              }
              defaultShowOnlySelected={false}
              options={[...history].sort((a, b) =>
                (b.StartDate || "").localeCompare(a.StartDate || "")
              )}
              onToggleSelect={onToggleAllEmployments}
              renderItem={(employment) => (
                <EmploymentSelectionListItem
                  key={employment.ID}
                  employment={employment}
                  employers={employers}
                  selected={isEmploymentSelected(employment.ID)}
                  onClick={() => onClickEmployment(employment.ID)}
                />
              )}
            />
          </div>

          {coworkerVerified ? (
            <div
              id="connection-panel-skills"
              role="tabpanel"
              aria-labelledby="connection-tab-skills"
              aria-hidden={tab !== "skills"}
              className={`cm-modal-tab-panel${tab === "skills" ? " is-active" : ""}`}
              {...(tab !== "skills" ? { inert: "" } : {})}
              data-testid="connection-endorsements"
            >
              <p className="fieldHint">
                Corroborate skills for this verified coworker. Updates overwrite your prior
                endorsement for that skill.
              </p>
              <h3>You endorsed</h3>
              {endorsementsGiven.length === 0 ? (
                <p className="byline">None yet.</p>
              ) : (
                <ul className="topList">
                  {endorsementsGiven.map((e) => (
                    <li key={e.Id ?? e.id}>
                      <span className="title">
                        {e.SkillName ?? e.skillName} —{" "}
                        {levelNames[e.SkillLevel ?? e.skillLevel] || e.SkillLevel || e.skillLevel}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <h3>They endorsed you</h3>
              {endorsementsReceived.length === 0 ? (
                <p className="byline">None yet.</p>
              ) : (
                <ul className="topList">
                  {endorsementsReceived.map((e) => (
                    <li key={e.Id ?? e.id}>
                      <span className="title">
                        {e.SkillName ?? e.skillName} —{" "}
                        {levelNames[e.SkillLevel ?? e.skillLevel] || e.SkillLevel || e.skillLevel}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <h3>Endorse / update</h3>
              <div className="field form-group">
                <label htmlFor="endorse-skill">Skill</label>
                <select
                  id="endorse-skill"
                  value={endorseSkillId}
                  onChange={(e) => setEndorseSkillId(e.target.value)}
                  data-testid="endorse-skill-select"
                >
                  <option value="">Select skill…</option>
                  {subjectSkills.map((s) => {
                    const sid = s.SkillId ?? s.skillId;
                    return (
                      <option key={sid} value={sid}>
                        {s.SkillName ?? s.skillName}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="field form-group">
                <label htmlFor="endorse-level">Level</label>
                <select
                  id="endorse-level"
                  value={endorseLevel}
                  onChange={(e) => setEndorseLevel(e.target.value)}
                  data-testid="endorse-level-select"
                >
                  {Object.entries(levelNames).map(([v, label]) => (
                    <option key={v} value={v}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <CustomButton
                type="button"
                variant="contained"
                color="primary"
                disabled={endorseBusy}
                onClick={saveEndorsement}
                data-testid="endorse-save"
              >
                {endorseBusy ? "Saving…" : "Save endorsement"}
              </CustomButton>
            </div>
          ) : null}
        </div>

        <FormActions
          doneLabel={busy ? "Saving…" : "Save"}
          onCancelAsync={async () => {
            close();
          }}
          onDeleteAsync={
            (connection.ID || connection.id) > 0
              ? async () => {
                  await onDelete();
                }
              : undefined
          }
          onSaveAsync={async () => {
            await onDoneAsync();
          }}
        />
      </Form>
    </div>
  );
}
